'use client';

/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react';

import { Box, Stack, Heading, useBreakpointValue } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import Slider from 'react-slick';

import { PrimaryButton } from '@components/custom-buttons';
import { Loader } from '@components/custom-loader';
import { CustomText } from '@components/Custom-text';
import { OrderDirection } from '@enums/index';
import { Mentor } from '@modules/auth/types';
import { useLazyGetMentorsQuery } from '@modules/find-mentor/api';
import MentorCard from '@modules/find-mentor/components/cards/MentorCard';
import { paths } from '@routes/path';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './carousel.css';

type ArrowProps = {
    className?: string;
    style?: React.CSSProperties;
    onClick?: () => void;
};

const NextArrow: React.FC<ArrowProps> = ({ className, style, onClick }) => (
    <Stack
        className={className}
        style={{ ...style, display: 'flex' }}
        onClick={onClick}
        sx={{
            position: 'absolute',
            top: '50%',
            right: '-50px',
            transform: 'translateY(-50%)',
            zIndex: 10,
            height: '50px',
            gap: 0,
            width: '50px',
            background: 'white',
            borderRadius: 'full',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'md',
        }}
    >
        <MdOutlineKeyboardArrowRight fontSize="30px" color="black" />
    </Stack>
);

const PrevArrow: React.FC<ArrowProps> = ({ className, style, onClick }) => (
    <Stack
        className={className}
        style={{ ...style, display: 'flex' }}
        onClick={onClick}
        sx={{
            position: 'absolute',
            top: '50%',
            left: '-50px',
            transform: 'translateY(-50%)',
            zIndex: 10,
            height: '50px',
            gap: 0,
            width: '50px',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            borderRadius: 'full',
            cursor: 'pointer',
            boxShadow: 'md',
        }}
    >
        <MdOutlineKeyboardArrowLeft fontSize="30px" color="black" />
    </Stack>
);

const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    useCss: true,
    swipe: true,
    swipeToSlide: true,
    slidesToShow: 5,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: 640,
            settings: {
                slidesToShow: 1,
                arrows: false,
            },
        },
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 2,
            },
        },
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 3,
            },
        },
        {
            breakpoint: 1280,
            settings: {
                slidesToShow: 5,
            },
        },
    ],
};

export const TopMentors = () => {
    const router = useRouter();
    const [getMentors, { data, isFetching }] = useLazyGetMentorsQuery();
    const isMobile = useBreakpointValue({ base: true, sm: false });

    useEffect(() => {
        getMentors({
            limit: process.env.NEXT_PUBLIC_FEATURED_MENTOR_LIST_LIMIT
                ? +process.env.NEXT_PUBLIC_FEATURED_MENTOR_LIST_LIMIT
                : 20,
            orderBy: 'priority',
            orderDirection: OrderDirection.Desc,
        });
    }, [getMentors]);

    const handleExploreAll = () => {
        router.push(paths.general.findMentor);
    };

    const renderMentors = () => {
        if (isFetching) {
            return <Loader height="10vh" width="100%" size="lg" />;
        }
        if (isMobile) {
            return data?.mentors?.map((mentor: Mentor) => (
                <Box key={mentor.id} px={2}>
                    <MentorCard mentorInfo={mentor} />
                </Box>
            ));
        }
        return (
            <Slider {...settings}>
                {data?.mentors?.map((mentor: Mentor) => (
                    <Box key={mentor.id} px={2}>
                        <MentorCard mentorInfo={mentor} />
                    </Box>
                ))}
            </Slider>
        );
    };
    return (
        <Stack>
            <Stack py={8} maxW="592px" mx="auto">
                <Heading textAlign="center" fontWeight="medium">
                    Discover India's Top Mentors
                </Heading>
                <CustomText color="shadowSlate" textAlign="center">
                    Unlock your potential with our mentorship program, connecting you with expert
                    mentors to upskill and seamlessly transition into your dream career. Empower
                    your journey with personalized guidance and industry insights.
                </CustomText>
            </Stack>

            <Stack>
                {renderMentors()}
                <Box py={3} textAlign="center">
                    <PrimaryButton
                        buttonLabel="Explore All Mentors"
                        onClick={handleExploreAll}
                        type="button"
                    />
                </Box>
            </Stack>
        </Stack>
    );
};
